import { RAuditConfig } from '@counsel-project/counsel-audit-backend-api'
import AddIcon from '@mui/icons-material/AddRounded'
import ArrowBackIcon from '@mui/icons-material/ArrowBackRounded'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { useCallback, useEffect, useMemo, useState } from 'react'
import usePageTitle from '../../../util/usePageTitle'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import PageContainer from '../../../components/layout/PageContainer'
import { auditBackendRequest } from '../../../util/api/audit-backend-api'
import checkToken from '../../../util/auth/checkToken'
import useStore from '../../../util/store/auth'
import AuditConfigEdit from '../AuditConfigEdit'
import Tooltip from '@mui/material/Tooltip'
import { getAuditConfigSavingProblems } from '../helpers'
import handleError from '../../../util/handleError'

const NewAuditConfigPage = () => {
  usePageTitle('New Audit Configuration')
  const navigate = useNavigate()
  const { directory } = useStore()

  const [config, setConfig] = useState<RAuditConfig | null>(null)
  const [loading, setLoading] = useState(false)

  const handleSave = useCallback(async () => {
    try {
      setLoading(true)

      if (!directory) return
      if (!config) return

      const token = await checkToken()
      if (!token) return

      const { result: newAuditConfig } = await auditBackendRequest.auditConfigs.create({
        token: '',
        directoryId: directory._id,
        state: config.state,
        programs: config.programs,
        enabled: config.enabled,
        displayComplianceCheckButton: config.displayComplianceCheckButton,
        autoComplianceCheck: config.autoComplianceCheck,
        title: config.title,
        minimumSeverityForRealTimeAlerts: config.minimumSeverityForRealTimeAlerts,
        measures: config.measures,
        locations: config.locations,
      })

      toast.success('Configuration created successfully')
      navigate(`/configure/${newAuditConfig._id}`)
    } catch (error) {
      handleError(error)
    } finally {
      setLoading(false)
    }
  }, [config, navigate, directory])

  useEffect(() => {
    if (directory) {
      setConfig({
        _id: Math.random().toString(36).substring(2, 15),
        directoryId: directory._id,
        state: 'CA',
        programs: [],
        enabled: true,
        displayComplianceCheckButton: true,
        autoComplianceCheck: true,
        title: 'New Compliance Criteria',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        minimumSeverityForRealTimeAlerts: 1,
        measures: [],
        locations: [],
      })
    } else {
      setConfig(null)
    }
  }, [directory])

  const saveProblems = useMemo(() => getAuditConfigSavingProblems(config), [config])

  if (!directory || !config) {
    return (
      <PageContainer>
        <Grid container justifyContent="center" alignItems="center" spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" textAlign="center">
              Please select an organization to continue
            </Typography>
          </Grid>
        </Grid>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-start' }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/configure')}
          data-qa-id="config-go-back-button"
        >
          Go Back
        </Button>
      </Box>
      <AuditConfigEdit auditConfig={config} onChangeAuditConfig={setConfig} />
      <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
        <Grid item>
          <Tooltip title={saveProblems.join(', ')}>
            <span>
              <Button
                onClick={handleSave}
                startIcon={<AddIcon />}
                disabled={loading || saveProblems.length > 0}
                data-qa-id="config-save-button"
              >
                {loading ? 'Saving...' : 'Save Configuration'}
              </Button>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </PageContainer>
  )
}

export default NewAuditConfigPage
